import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import { apiServices } from "../../../services/apiServices";
import Swal from 'sweetalert2';

const EntityReferralStatistics = () => {
  const [paidAmount, setPaidAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [pendingAmount, setPendingAmount] = useState("0.00");
  const [currentMonthReferrals, setCurrentMonthReferrals] = useState("0");
  const [currentMonthAmount, setCurrentMonthAmount] = useState("0.00");

  // Get current month and year
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11, so add 1
  const currentYear = currentDate.getFullYear();

  const months = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" }
  ];

  const cardStyle = {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e9ecef',
    width:"100%",
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
      borderColor: '#214F7D',
    }
  };

  const sectionStyle = {
    backgroundColor: '#fff',
    border: '1px solid #e9ecef',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  };

  const formRowStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
    '& label': {
      width: '200px',
      fontWeight: 500,
      color: '#495057',
    },
    '& span': {
      padding: '8px 12px',
      backgroundColor: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '6px',
      minWidth: '150px',
    }
  };

  const inputStyle = {
    width: '300px',
    padding: '8px 12px',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    fontSize: '14px',
    '&:focus': {
      outline: 'none',
      borderColor: '#214F7D',
      boxShadow: '0 0 0 2px rgba(33, 79, 125, 0.2)',
    }
  };

  const saveButtonStyle = {
    backgroundColor: '#214F7D',
    color: 'white',
    padding: '10px 24px',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#1a3f63',
      transform: 'translateY(-1px)',
    }
  };

  const tableHeaderStyle = {
    backgroundColor: '#f8f9fa',
    '& th': {
      fontWeight: 600,
      color: '#214F7D',
      fontSize: '14px',
      padding: '12px',
      borderBottom: '2px solid #dee2e6',
    }
  };

  const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #dee2e6',
    fontSize: '14px',
  };

  const statusCompleteStyle = {
    backgroundColor: '#d4edda',
    color: '#155724',
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600,
  };

  const statusPendingStyle = {
    backgroundColor: '#fff3cd',
    color: '#856404',
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600,
  };
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `.swal2-container { z-index: 999999 !important; }`;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Fetch payout summary data on component mount
  useEffect(() => {
    fetchPayoutSummary();
  }, []);

  const fetchPayoutSummary = async () => {
    try {
      const response = await apiServices.postAPI('admin/get-payout-summary', {
        peacekeeper_id: 'IN-0000021-IAPM',
        year: currentYear,
        month: currentMonth
      });

      if (response.data.success) {
        // Process the payout history data
        const historyData = response.data.data || [];
        setPayoutHistory(historyData);

        // Calculate pending amount and current month data from current month data
        const currentMonthData = historyData.find(item => 
          item.month === currentMonth && item.year === currentYear
        );
        
        if (currentMonthData) {
          setPendingAmount(currentMonthData.balance || "0.00");
          setCurrentMonthReferrals(currentMonthData.referrals || "0");
          setCurrentMonthAmount(currentMonthData.amount || "0.00");
        }
      } else {
        setAlert({ show: true, type: "error", message: response.data.message || "Failed to fetch payout summary." });
      }
    } catch (error) {
      console.error("Failed to fetch payout summary:", error);
      setAlert({ show: true, type: "error", message: "Failed to fetch payout summary. Please try again." });
    }
  };
  const handleSave = async () => {
    // Basic validation
    if (!paidAmount.trim()) {
      setAlert({ show: true, type: "error", message: "Please enter the paid referral amount." });
      return;
    }

    // Validate that paid amount doesn't exceed pending amount
    const paidNum = parseFloat(paidAmount);
    const pendingNum = parseFloat(pendingAmount);
    
    if (paidNum > pendingNum) {
      setAlert({ show: true, type: "error", message: `Paid amount cannot exceed pending amount of $${pendingAmount}.` });
      return;
    }

    if (!remarks.trim()) {
      setAlert({ show: true, type: "error", message: "Please enter remarks/transaction ID." });
      return;
    }

    if (!receiptFile) {
      setAlert({ show: true, type: "error", message: "Please upload a receipt file." });
      return;
    }

    setLoading(true);
    setAlert({ show: false, type: "", message: "" });

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('peacekeeper_id', 'IN-0000021-IAPM'); // This should be dynamic based on the logged-in user
      formData.append('year', currentYear.toString());
      formData.append('month', currentMonth.toString());
      formData.append('paid_amount', paidAmount);
      formData.append('payout_status', 'Completed'); // Assuming completed when saving
      formData.append('remarks', remarks);
      formData.append('receipt_file', receiptFile);

      const response = await apiServices.postAPI('admin/referral-payout', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      if (response.data.success) {
        // Show success message with SweetAlert
        Swal.fire({
          title: 'Success!',
          text: response.data.message || 'Referral payout saved successfully!',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#214F7D',
          didOpen: () => {
            document.querySelector('.swal2-container').style.zIndex = '999999';
          }
        });

        // Add a new row for the payment instead of updating existing row
        const newPaymentEntry = {
          month: currentMonth,
          year: currentYear,
          peacekeeper_id: currentMonthReferrals,
          refferal_amount: currentMonthAmount,
          paid: paidAmount,
          pending_amount: "0.00",
          status: "Completed",
          attachment: response.data.data?.attachment_url || null
        };
        
        setPayoutHistory(prevHistory => [newPaymentEntry, ...prevHistory]);

        // Update pending amount in the form
        const newPendingAmount = Math.max(0, parseFloat(pendingAmount) - parseFloat(paidAmount));
        setPendingAmount(newPendingAmount.toFixed(2));

        // Clear form fields after successful submission
        setPaidAmount("");
        setRemarks("");
        setReceiptFile(null);
      } else {
        setAlert({ show: true, type: "error", message: response.data.message || "Failed to save referral payout." });
      }
    } catch (error) {
      console.error("Referral payout submission failed:", error);
      setAlert({ show: true, type: "error", message: "Failed to save referral payout. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ padding: '24px', fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      
      {/* Top Cards */}
      <Grid container spacing={3} sx={{ marginBottom: '32px' }}>
        <Grid item xs={12} md={6}>
          <Card sx={cardStyle}>
            <CardContent>
              <Typography 
                variant="body2" 
                sx={{ 
                  margin: '0 0 12px 0',
                  fontSize: '14px',
                  color: '#666',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Total Referrals (Lifetime)
              </Typography>
              <Typography 
                variant="h3" 
                sx={{ 
                  margin: 0,
                  fontSize: '32px',
                  fontWeight: 700,
                  color: '#214F7D'
                }}
              >
                {payoutHistory.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={cardStyle}>
            <CardContent>
              <Typography 
                variant="body2" 
                sx={{ 
                  margin: '0 0 12px 0',
                  fontSize: '14px',
                  color: '#666',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Total Referral Earnings
              </Typography>
              <Typography 
                variant="h3" 
                sx={{ 
                  margin: 0,
                  fontSize: '32px',
                  fontWeight: 700,
                  color: '#214F7D'
                }}
              >
                $30
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Current Month Section */}
      <Box sx={sectionStyle}>
        <Typography 
          variant="h5" 
          sx={{ 
            color: '#214F7D',
            margin: '0 0 20px 0',
            fontSize: '20px',
            fontWeight: 600
          }}
        >
          Current Month: {months.find(m => m.value === currentMonth.toString())?.label} {currentYear}
        </Typography>

        <Box sx={formRowStyle}>
          <label>Current Month Referrals:</label>
          <span style={{ 
            padding: '8px 12px',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            minWidth: '150px',
            color: '#495057',
            fontWeight: 500
          }}>
            {currentMonthReferrals || "0"}
          </span>
        </Box>

        <Box sx={formRowStyle}>
          <label>Current Month Amount:</label>
          <span style={{ 
            padding: '8px 12px',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            minWidth: '150px',
            color: '#495057',
            fontWeight: 500
          }}>
            ${currentMonthAmount}
          </span>
        </Box>

        

        {/* Amounts and Payment Status Row */}
        <Box sx={{ display: 'flex', gap: 2, marginBottom: '16px', alignItems: 'center', flexWrap: 'nowrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '250px', }}>
            <label style={{ width: '140px', fontWeight: 500, color: '#495057', whiteSpace: 'nowrap', marginRight: '16px' }}>Paid Referral Amount:</label>
            <TextField
              value={paidAmount}
              onChange={(e) => setPaidAmount(e.target.value)}
              type="number" 
              placeholder="Enter amount"
              sx={{
                width: '150px',
                '& .MuiInputBase-input': {
                  padding: '8px 12px',
                  backgroundColor: 'white',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  fontSize: '14px',
                },
                '&:hover': {
                  borderColor: '#214F7D',
                },
                '&.Mui-focused': {
                  borderColor: '#214F7D',
                  boxShadow: '0 0 0 2px rgba(33, 79, 125, 0.2)',
                }
              }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '200px' }}>
            <label style={{ width: '110px', fontWeight: 500, color: '#495057', whiteSpace: 'nowrap', marginRight: '16px' }}>Pending Amount:</label>
            <span style={{ 
              padding: '8px 12px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '6px',
              minWidth: '80px',
              color: '#dc3545',
              fontWeight: 600
            }}>
              ${pendingAmount}
            </span>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '200px' }}>
            <label style={{ width: '110px', fontWeight: 500, color: '#495057', whiteSpace: 'nowrap', marginRight: '8px' }}>Payment Status:</label>
            <Chip 
              label={paidAmount === pendingAmount ? "Completed" : "Pending"}
              color={paidAmount === pendingAmount ? "success" : "error"}
              variant="outlined"
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                height: '32px',
                minWidth: '80px'
              }}
            />
          </Box>
        </Box>

        <Box sx={formRowStyle}>
          <label>Remark (Transaction ID):</label>
          <TextField
            fullWidth={false}
            placeholder="Enter transaction ID"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            sx={{
              width: '300px',
              '& .MuiInputBase-input': inputStyle
            }}
          />
        </Box>

        <Box sx={formRowStyle}>
          <label>Attachment:</label>
          <input 
            type="file" 
            accept="image/*,.pdf,.doc,.docx"
            onChange={(e) => setReceiptFile(e.target.files[0])}
            style={{
              padding: '8px',
              border: '1px solid #dee2e6',
              borderRadius: '6px',
              backgroundColor: 'white',
              fontSize: '14px'
            }}
          />
        </Box>

        <Button 
          variant="contained" 
          sx={saveButtonStyle}
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? "Saving..." : "Save"}
        </Button>

        {alert.show && (
          <Alert severity={alert.type} sx={{ mt: 2 }}>
            {alert.message}
          </Alert>
        )}
      </Box>

      {/* Payout History */}
      <Box sx={sectionStyle}>
        <Typography 
          variant="h5" 
          sx={{ 
            color: '#214F7D',
            margin: '0 0 20px 0',
            fontSize: '20px',
            fontWeight: 600
          }}
        >
          Payout History
        </Typography>

        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #dee2e6' }}>
          <Table>
            <TableHead sx={tableHeaderStyle}>
              <TableRow>
                <TableCell>Month</TableCell>
                <TableCell>Referrals</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Paid</TableCell>
                <TableCell>Pending</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Attachment</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payoutHistory.length > 0 ? (
                payoutHistory.map((item, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                    <TableCell sx={tableCellStyle}>{months.find(m => m.value === item.month.toString())?.label || item.month}</TableCell>
                    <TableCell sx={tableCellStyle}>{item.peacekeeper_id}</TableCell>
                    <TableCell sx={tableCellStyle}>{item.refferal_amount}</TableCell>
                    <TableCell sx={tableCellStyle}>{item.paid}</TableCell>
                    <TableCell sx={tableCellStyle}>{item.pending_amount}</TableCell>
                    <TableCell sx={tableCellStyle}>
                      <Chip 
                        label={item.status || (item.paid > 0 ? "Completed" : "Pending")}
                        color={(item.status === "Completed" || item.paid > 0) ? "success" : "error"}
                        variant="outlined"
                        sx={{
                          fontWeight: 600,
                          fontSize: '12px',
                          height: '28px',
                          minWidth: '70px'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={tableCellStyle}>
                      {item.attachment ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => window.open(item.attachment, '_blank')}
                          sx={{
                            borderColor: '#214F7D',
                            color: '#214F7D',
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: '#1a3f63',
                              backgroundColor: '#f8f9fa'
                            }
                          }}
                        >
                          View Attachment
                        </Button>
                      ) : (
                        <span style={{ color: '#6c757d', fontStyle: 'italic' }}>No attachment</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                    No payout history available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default EntityReferralStatistics;
